<template>
  <section class="section" v-if="mostPlayed && mostPlayed.length > 0">
    <div class="section-header">
      <h2 class="section-title medium">{{ $t('sections.mostPlayed') }}</h2>
      <div class="navigation">
        <button class="see-all">{{ $t('common.seeAll') }}</button>
        <div class="nav-buttons">
          <NavButtons 
            @onPrevious="scrollMostPlayedLeft"
            @onNext="scrollMostPlayedRight"
          />
        </div>
      </div>
    </div>
    <div class="cards-container" ref="mostPlayedRef">
      <LotteryCard
        v-for="game in displayedMostPlayed"
        :key="game.id"
        :lottery="game"
        layout="horizontal"
        :isFavorited="isFavorited"
        @favorite="handleFavorite(game)"
        @bet="handleBet(game)"
      />
    </div>

    <div v-if="hasMore" class="load-more-container">
      <button class="load-more-button" @click="loadMore">
        {{ $t('forms.loadMoreNumberGames') }}
      </button>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, defineProps, defineEmits } from "vue";
import LotteryCard from "@/components/LotteryCard.vue";
import NavButtons from "@/components/base/NavButtons.vue";
import type { UpcomingDraw } from '@/types/lottery';
import { iframeCommunication } from "@/utils/iframeCommunication";

const mostPlayedRef = ref<HTMLElement | null>(null);
const displayLimit = ref(12);

const props = defineProps<{
  mostPlayed: UpcomingDraw[]
  isFavorited?: (draw: UpcomingDraw) => boolean
}>();

const emit = defineEmits<{
  (e: 'bet', game: UpcomingDraw): void;
  (e: 'favorite', game: UpcomingDraw): void;
}>();

const displayedMostPlayed = computed(() => props.mostPlayed.slice(0, displayLimit.value));

const hasMore = computed(() => props.mostPlayed.length > displayLimit.value);

const loadMore = () => {
  displayLimit.value += 10;
  nextTick(() => {
    setTimeout(() => {
      iframeCommunication.sendImmediateHeightUpdate();
    }, 100);
  });
};

const scrollMostPlayedLeft = () => {
  if (mostPlayedRef.value) {
    const cardWidth = mostPlayedRef.value.offsetWidth;
    mostPlayedRef.value.scrollBy({ left: -cardWidth, behavior: "smooth" });
  }
};

const scrollMostPlayedRight = () => {
  if (mostPlayedRef.value) {
    const cardWidth = mostPlayedRef.value.offsetWidth;
    mostPlayedRef.value.scrollBy({ left: cardWidth, behavior: "smooth" });
  }
};

const handleBet = (lottery: UpcomingDraw) => {
  emit('bet', lottery);
};

const handleFavorite = (game: UpcomingDraw) => {
  emit('favorite', game);
};
</script>

<script lang="ts">
export default {
  name: 'MostPlayedSection'
}
</script>

<style scoped>
.section {
  margin-bottom: 48px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.navigation {
  display: flex;
  align-items: center;
  gap: 16px;
}

.nav-buttons {
  display: flex;
  align-items: center;
}


.see-all {
  background: none;
  border: none;
  color: var(--text-primary);
  font-size: 16px;
  cursor: pointer;
  padding: 8px 0px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.see-all:hover {
  background: rgba(255, 255, 255, 0.1);
}

.cards-container {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 8px 0;
  scroll-snap-type: x mandatory;
}

.cards-container > * {
  flex: 0 0 calc(50% - 6px);
  scroll-snap-align: start;
}

.cards-container::-webkit-scrollbar {
  display: none;
}

@media (max-width: 768px) {
  .section {
    padding: 0;
    margin: 0 0 32px 0;
  }

  .section-header {
    /* padding: 0 8px; */
    margin-bottom: 16px;
  }

  .cards-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0;
    margin: 0;
    overflow-x: visible;
    scroll-snap-type: none;
    height: auto;
  }

  .cards-container > * {
    flex: none;
    scroll-snap-align: none;
    width: 100%;
    height: auto;
  }

  .see-all {
    display: none;
  }
}
</style> 